import * as XLSX from 'xlsx-js-style';
import { Supplier } from '@/types/supplier';
import { WarehouseReceipt, WarehouseReceiptLine } from '@/types/warehouseReceipt';
import { PaymentVoucherResponse } from '@/types/paymentVoucher';
import { DebtAdjustmentVoucherResponse } from '@/types/debtAdjustmentVoucher';
import { ExcelCellStyle, ExcelBorderStyle, ExcelAlignmentStyle, ExcelBorderConfig } from '@/types/excel';

interface ExportOptions {
  supplier: Supplier;
  warehouseReceipts: WarehouseReceipt[];
  warehouseReceiptLines: WarehouseReceiptLine[];
  paymentVouchers: PaymentVoucherResponse[];
  debtAdjustmentVouchers?: DebtAdjustmentVoucherResponse[];
  dateFrom: string;
  dateTo: string;
  t: (key: string) => string;
}


export class SupplierDebtExcelExport {
  private options: ExportOptions;
  private worksheet: XLSX.WorkSheet;
  private currentRow: number;
  private headerRowStart: number = 0;
  private dataRowStart: number = 0;
  private dataRowEnd: number = 0;
  private companyRowNumber: number = 0;
  private titleRowNumber: number = 0;
  private dateRangeRowNumber: number = 0;

  constructor(options: ExportOptions) {
    this.options = options;
    this.worksheet = {};
    this.currentRow = 1;
  }

  /**
   * Generates Excel file with formatted debt report
   */
  public async generateExcel(): Promise<Blob> {
    console.log('Starting Excel generation with data:', {
      supplier: this.options.supplier.name,
      warehouseReceiptsCount: this.options.warehouseReceipts.length,
      warehouseReceiptLinesCount: this.options.warehouseReceiptLines.length,
      dateRange: `${this.options.dateFrom} to ${this.options.dateTo}`
    });

    // Initialize worksheet
    this.worksheet = {};
    this.currentRow = 1;

    // Add fixed header rows (3 rows)
    this.addFixedHeaders();

    // Add title row with file name and date range
    this.addTitleRow();

    // Add supplier information
    this.addSupplierInfo();

    // Add detailed debt table
    this.addDebtTable();

    // Add signature section
    this.addSignatureSection();

    // Apply formatting after all content is added
    this.applyFormatting();

    console.log('Worksheet cells created:', Object.keys(this.worksheet).length);
    console.log('Final worksheet structure sample:', Object.keys(this.worksheet).slice(0, 10));

    // Ensure worksheet has proper range
    if (Object.keys(this.worksheet).length > 0) {
      const range = XLSX.utils.decode_range(this.worksheet['!ref'] || 'A1:I' + this.currentRow);
      this.worksheet['!ref'] = XLSX.utils.encode_range(range);
      console.log('Worksheet range set to:', this.worksheet['!ref']);
    } else {
      console.warn('Worksheet is empty, setting minimal range');
      this.worksheet['!ref'] = 'A1:I1';
    }

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, this.worksheet, 'Debt Report');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    console.log('Excel blob created, size:', blob.size, 'bytes');
    return blob;
  }

  private addFixedHeaders(): void {
    // Row 1: Company name - store row number for formatting
    this.companyRowNumber = this.currentRow;
    this.setCellValue('A', this.currentRow, 'phuongnamprolab');
    this.currentRow++;

    // Row 2: Address
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.debtExport.address'));
    this.setCellValue('B', this.currentRow, 'Quận Hai Bà Trưng, Hà Nội');
    this.currentRow++;

    // Row 3: Phone
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.debtExport.phone'));
    this.setCellValue('B', this.currentRow, '0915241818');
    this.currentRow++;
  }

  /**
   * Adds title row with merged cells and center alignment for title and date range
   */
  private addTitleRow(): void {
    this.currentRow++; // Empty row

    // Title row - merge cells A to I and apply formatting
    const title = this.options.t('suppliers.debtExport.reportTitle');
    this.titleRowNumber = this.currentRow;
    this.setCellValue('A', this.titleRowNumber, title);

    // Add merge range for title row (A to I)
    if (!this.worksheet['!merges']) {
      this.worksheet['!merges'] = [];
    }
    this.worksheet['!merges'].push({
      s: { c: 0, r: this.titleRowNumber - 1 }, // Start: column A (0), row (0-based)
      e: { c: 8, r: this.titleRowNumber - 1 }  // End: column I (8), row (0-based)
    });

    this.currentRow++;

    // Date range row - merge cells A to I and apply formatting
    const dateRange = `${this.options.t('suppliers.debtExport.from')} ${this.formatDate(this.options.dateFrom)} ${this.options.t('suppliers.debtExport.to')} ${this.formatDate(this.options.dateTo)}`;
    this.dateRangeRowNumber = this.currentRow;
    this.setCellValue('A', this.dateRangeRowNumber, dateRange);

    // Add merge range for date range row (A to I)
    this.worksheet['!merges'].push({
      s: { c: 0, r: this.dateRangeRowNumber - 1 }, // Start: column A (0), row (0-based)
      e: { c: 8, r: this.dateRangeRowNumber - 1 }  // End: column I (8), row (0-based)
    });

    this.currentRow += 2; // Extra spacing
  }

  private addSupplierInfo(): void {
    const { supplier } = this.options;

    // Supplier name
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.fields.name') + ':');
    this.setCellValue('B', this.currentRow, supplier.name);
    this.setCellValue('F', this.currentRow, this.options.t('suppliers.debtExport.initialDebt') + ':');
    // Use openingBalance from supplier data, default to 0 if not provided
    this.setCellValueWithFormat('H', this.currentRow, supplier.openingBalance || 0, 'currency');
    this.currentRow++;

    // Supplier code
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.fields.code') + ':');
    this.setCellValue('B', this.currentRow, supplier.code);
    this.setCellValue('F', this.currentRow, this.options.t('suppliers.debtExport.periodDebt') + ':');
    this.setCellValueWithFormat('G', this.currentRow, this.calculateTotalDebt(), 'currency');
    this.setCellValueWithFormat('H', this.currentRow, this.calculatePaidAmount(), 'currency');
    this.currentRow++;

    // Phone
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.fields.phone') + ':');
    this.setCellValue('B', this.currentRow, supplier.phone || '');
    this.setCellValue('F', this.currentRow, this.options.t('suppliers.debtExport.endDebt') + ':');
    // End debt is the sum of opening balance and debt from the period
    this.setCellValueWithFormat('G', this.currentRow, (supplier.openingBalance || 0) + (supplier.currentPayables || 0), 'currency');
    this.currentRow += 2; // Extra spacing
  }

  private addDebtTable(): void {
    console.log('Adding debt table with receipts:', this.options.warehouseReceipts.length, 'and payment vouchers:', this.options.paymentVouchers.length);

    // Store header row position for formatting
    this.headerRowStart = this.currentRow;

    // Table headers
    const headers = [
      this.options.t('suppliers.debtExport.time'),
      this.options.t('suppliers.debtExport.code'),
      this.options.t('suppliers.debtExport.description'),
      this.options.t('suppliers.debtExport.unit'),
      this.options.t('suppliers.debtExport.quantity'),
      this.options.t('suppliers.debtExport.unitPrice'),
      this.options.t('suppliers.debtExport.amount'),
      this.options.t('suppliers.debtExport.note'),
      this.options.t('suppliers.debtExport.memo')
    ];

    // Add header row
    headers.forEach((header, index) => {
      this.setCellValue(this.getColumnLetter(index), this.currentRow, header);
    });
    this.currentRow++;

    // Store data start position
    this.dataRowStart = this.currentRow;

    // Combine warehouse receipts, payment vouchers, and debt adjustments, then sort by date
    type CombinedRecord = 
      | { type: 'receipt'; data: WarehouseReceipt; date: string }
      | { type: 'voucher'; data: PaymentVoucherResponse; date: string }
      | { type: 'adjustment'; data: DebtAdjustmentVoucherResponse; date: string };

    const combinedRecords: CombinedRecord[] = [
      ...this.options.warehouseReceipts.map(receipt => ({
        type: 'receipt' as const,
        data: receipt,
        date: receipt.receipt_date || receipt.created_at
      })),
      ...this.options.paymentVouchers.map(voucher => ({
        type: 'voucher' as const,
        data: voucher,
        date: voucher.payment_date
      })),
      ...(this.options.debtAdjustmentVouchers || []).map(adjustment => ({
        type: 'adjustment' as const,
        data: adjustment,
        date: adjustment.createdAt
      }))
    ];

    // Sort by date (oldest first)
    combinedRecords.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Add records to table
    combinedRecords.forEach(record => {
      if (record.type === 'receipt') {
        console.log(`Processing receipt: ${record.data.code} with amount: ${record.data.total_amount}`);
        this.addWarehouseReceiptRows(record.data);
      } else if (record.type === 'voucher') {
        console.log(`Processing payment voucher: ${record.data.code} with amount: ${record.data.amount}`);
        this.addPaymentVoucherRow(record.data);
      } else {
        console.log(`Processing debt adjustment: ${record.data.code} with amount: ${record.data.adjustment_amount}`);
        this.addDebtAdjustmentRow(record.data);
      }
    });

    // Store data end position
    this.dataRowEnd = this.currentRow - 1;

    console.log('Debt table completed, current row:', this.currentRow);
  }

  private addWarehouseReceiptRows(receipt: WarehouseReceipt): void {
    // Add receipt header row - total_amount goes to "Ghi nợ" column (H) instead of "Thành tiền" column (G)
    this.setCellValue('A', this.currentRow, this.formatDate(receipt.receipt_date || receipt.created_at));
    this.setCellValue('B', this.currentRow, receipt.code);
    this.setCellValue('C', this.currentRow, this.options.t('warehouseReceipts.import'));
    this.setCellValueWithFormat('H', this.currentRow, this.requiredAmount(receipt.total_amount), 'currency');
    this.currentRow++;

    // Add receipt line items - use receipt.code for filtering since that's what we use in the API call
    const receiptLines = this.options.warehouseReceiptLines.filter(line =>
      line.warehouse_receipt_id === receipt.code
    );

    console.log(`Adding lines for receipt ${receipt.code}: found ${receiptLines.length} lines`);

    receiptLines.forEach(line => {
      this.setCellValue('B', this.currentRow, line.code);
      this.setCellValue('C', this.currentRow, line.material_name);
      this.setCellValue('D', this.currentRow, line.material_unit);
      this.setCellValueWithFormat('E', this.currentRow, line.quantity, 'number');
      this.setCellValueWithFormat('F', this.currentRow, this.requiredAmount(line.unit_price), 'currency');
      this.setCellValueWithFormat('G', this.currentRow, this.requiredAmount(line.total_price), 'currency');
      this.currentRow++;
    });
  }

  private addPaymentVoucherRow(voucher: PaymentVoucherResponse): void {
    // Add payment voucher row - amount goes to "Ghi có" column (I)
    this.setCellValue('A', this.currentRow, this.formatDate(voucher.payment_date));
    this.setCellValue('B', this.currentRow, voucher.code);
    this.setCellValue('C', this.currentRow, this.options.t('paymentVouchers.titleSingle'));
    this.setCellValueWithFormat('I', this.currentRow, voucher.amount, 'currency'); // Payment voucher amount goes to "Ghi có" column
    this.currentRow++;
  }

  private addDebtAdjustmentRow(adjustment: DebtAdjustmentVoucherResponse): void {
    // Add debt adjustment row
    // Columns: A=Time, B=Code, C=Description, D=Unit, E=Quantity, F=UnitPrice, G=Amount, H=Note (Ghi nợ), I=Memo (Ghi có)
    // Warehouse receipts: total_amount goes to H (Ghi nợ)
    // Payment vouchers: amount goes to I (Ghi có)
    // Debt adjustments: 
    //   - Negative adjustment (decrease debt) goes to I (Ghi có) - same as payment vouchers
    //   - Positive adjustment (increase debt) goes to H (Ghi nợ) - same as warehouse receipts
    this.setCellValue('A', this.currentRow, this.formatDate(adjustment.createdAt));
    this.setCellValue('B', this.currentRow, adjustment.code);
    // Put description with reason in column C
    const description = adjustment.reason 
      ? `${this.options.t('debtAdjustmentVoucher.titleSingle')} - ${adjustment.reason}`
      : this.options.t('debtAdjustmentVoucher.titleSingle');
    this.setCellValue('C', this.currentRow, description);
    if (adjustment.adjustment_amount < 0) {
      // Negative adjustment (decrease debt) goes to "Ghi có" column (I - Memo)
      this.setCellValueWithFormat('I', this.currentRow, Math.abs(adjustment.adjustment_amount), 'currency');
    } else {
      // Positive adjustment (increase debt) goes to "Ghi nợ" column (H - Note)
      this.setCellValueWithFormat('H', this.currentRow, adjustment.adjustment_amount, 'currency');
    }
    this.currentRow++;
  }

  private addSignatureSection(): void {
    this.currentRow += 2; // Extra spacing

    // Date
    const today = new Date();
    this.setCellValue('H', this.currentRow, `${this.options.t('suppliers.debtExport.date')} ${today.getDate()} ${this.options.t('suppliers.debtExport.month')} ${today.getMonth() + 1} ${this.options.t('suppliers.debtExport.year')} ${today.getFullYear()}`);
    this.currentRow += 2;

    // Signature fields
    this.setCellValue('A', this.currentRow, this.options.t('suppliers.debtExport.supplier'));
    this.setCellValue('D', this.currentRow, this.options.t('suppliers.debtExport.preparedBy'));
    this.setCellValue('H', this.currentRow, this.options.t('suppliers.debtExport.company'));
    this.currentRow++;

    this.setCellValue('A', this.currentRow, `(${this.options.t('suppliers.debtExport.signAndName')})`);
    this.setCellValue('D', this.currentRow, `(${this.options.t('suppliers.debtExport.signAndName')})`);
    this.setCellValue('H', this.currentRow, `(${this.options.t('suppliers.debtExport.signAndName')})`);
  }

  private setCellValue(column: string, row: number, value: string | number): void {
    const cellAddress = `${column}${row}`;
    this.worksheet[cellAddress] = { v: value, t: typeof value === 'number' ? 'n' : 's' };

    // Update worksheet range
    this.updateWorksheetRange(cellAddress);
  }

  private setCellValueWithFormat(column: string, row: number, value: number, format: 'currency' | 'number'): void {
    const cellAddress = `${column}${row}`;
    const formattedValue = format === 'currency' ? this.formatCurrency(value) : this.formatNumber(value);

    // Store as string with Vietnamese formatting (dots for thousands)
    this.worksheet[cellAddress] = {
      v: formattedValue,
      t: 's' // String type to preserve Vietnamese formatting
    };

    // Update worksheet range
    this.updateWorksheetRange(cellAddress);
  }

  private updateWorksheetRange(cellAddress: string): void {
    if (!this.worksheet['!ref']) {
      this.worksheet['!ref'] = `${cellAddress}:${cellAddress}`;
    } else {
      const range = XLSX.utils.decode_range(this.worksheet['!ref']);
      const cellRef = XLSX.utils.decode_cell(cellAddress);

      if (cellRef.c < range.s.c) range.s.c = cellRef.c;
      if (cellRef.c > range.e.c) range.e.c = cellRef.c;
      if (cellRef.r < range.s.r) range.s.r = cellRef.r;
      if (cellRef.r > range.e.r) range.e.r = cellRef.r;

      this.worksheet['!ref'] = XLSX.utils.encode_range(range);
    }
  }

  private getColumnLetter(index: number): string {
    return String.fromCharCode(65 + index); // A, B, C, etc.
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }

  private calculateTotalDebt(): number {
    return this.options.warehouseReceipts.reduce((sum, receipt) => sum + this.requiredAmount(receipt.total_amount), 0);
  }

  private requiredAmount(value?: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Incomplete supplier debt data');
    return value;
  }

  private calculatePaidAmount(): number {
    // Calculate total paid amount from payment vouchers
    return this.options.paymentVouchers.reduce((sum, voucher) => sum + voucher.amount, 0);
  }

  /**
   * Applies Excel formatting including column widths, header styling, borders, and alignment
   */
  private applyFormatting(): void {
    // Set column widths for better readability
    this.worksheet['!cols'] = [
      { wch: 10 }, // A - Time
      { wch: 12 }, // B - Code
      { wch: 24 }, // C - Description
      { wch: 10 }, // D - Unit
      { wch: 10 }, // E - Quantity
      { wch: 15 }, // F - Unit Price
      { wch: 12 }, // G - Amount
      { wch: 12 }, // H - Note
      { wch: 20 }, // I - Memo
    ];

    // Apply company name formatting
    this.applyCompanyNameFormatting();

    // Apply title row formatting
    this.applyTitleFormatting();

    // Apply header row formatting if we have a header
    if (this.headerRowStart > 0) {
      this.applyHeaderFormatting();
    }

    // Apply data row formatting if we have data
    if (this.dataRowStart > 0 && this.dataRowEnd >= this.dataRowStart) {
      this.applyDataFormatting();
    }

    // Apply borders to the entire table
    if (this.headerRowStart > 0 && this.dataRowEnd >= this.headerRowStart) {
      this.applyTableBorders();
    }
  }

  /**
   * Applies bold formatting to company name
   */
  private applyCompanyNameFormatting(): void {
    if (this.companyRowNumber === 0) return;
    
    const cellAddress = `A${this.companyRowNumber}`;
    if (this.worksheet[cellAddress]) {
      // Ensure cell has proper structure before applying styles
      if (!this.worksheet[cellAddress].s) {
        this.worksheet[cellAddress].s = {};
      }

      const companyNameStyle: ExcelCellStyle = {
        font: { bold: true, sz: 12 }
      };
      this.worksheet[cellAddress].s = companyNameStyle;
    }
  }

  /**
   * Applies formatting to title rows with center alignment and bold formatting for title
   */
  private applyTitleFormatting(): void {
    // Apply title formatting using stored row number
    if (this.titleRowNumber > 0) {
      const cellAddress = `A${this.titleRowNumber}`;
      if (this.worksheet[cellAddress]) {
        // Ensure cell has proper structure before applying styles
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        const titleStyle: ExcelCellStyle = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center", vertical: "center" }
        };
        this.worksheet[cellAddress].s = titleStyle;
      }
    }

    // Apply date range formatting using stored row number
    if (this.dateRangeRowNumber > 0) {
      const cellAddress = `A${this.dateRangeRowNumber}`;
      if (this.worksheet[cellAddress]) {
        // Ensure cell has proper structure before applying styles
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        const dateRangeStyle: ExcelCellStyle = {
          alignment: { horizontal: "center", vertical: "center" }
        };
        this.worksheet[cellAddress].s = dateRangeStyle;
      }
    }
  }

  /**
   * Applies formatting to header row
   */
  private applyHeaderFormatting(): void {
    for (let col = 0; col < 9; col++) {
      const cellAddress = `${this.getColumnLetter(col)}${this.headerRowStart}`;
      if (this.worksheet[cellAddress]) {
        const borderStyle: ExcelBorderStyle = { style: "thin", color: { rgb: "000000" } };
        const headerStyle: ExcelCellStyle = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { patternType: "solid", fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: borderStyle,
            bottom: borderStyle,
            left: borderStyle,
            right: borderStyle
          }
        };
        this.worksheet[cellAddress].s = headerStyle;
      }
    }
  }

  /**
   * Applies formatting to data rows
   */
  private applyDataFormatting(): void {
    for (let row = this.dataRowStart; row <= this.dataRowEnd; row++) {
      for (let col = 0; col < 9; col++) {
        const cellAddress = `${this.getColumnLetter(col)}${row}`;
        if (this.worksheet[cellAddress]) {
          // Determine alignment based on column content
          const alignment: ExcelAlignmentStyle = { vertical: "center" };

          // Right-align numeric columns (E, F, G, H)
          if (col >= 4 && col <= 7) {
            alignment.horizontal = "right";
          } else {
            alignment.horizontal = "left";
          }

          const dataStyle: ExcelCellStyle = {
            alignment
          };

          this.worksheet[cellAddress].s = dataStyle;
        }
      }
    }
  }

  /**
   * Applies continuous border lines to all cells in the debt table
   */
  private applyTableBorders(): void {
    const startCol = 0;
    const endCol = 8;
    const startRow = this.headerRowStart;
    const endRow = this.dataRowEnd;

    // Apply borders to all cells in the table
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cellAddress = `${this.getColumnLetter(col)}${row}`;

        // Initialize cell if it doesn't exist
        if (!this.worksheet[cellAddress]) {
          this.worksheet[cellAddress] = { v: '', t: 's' };
        }

        // Initialize style object if it doesn't exist
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        // Define border styles with proper typing
        const thinBorderStyle: ExcelBorderStyle = { style: "thin", color: { rgb: "000000" } };
        const mediumBorderStyle: ExcelBorderStyle = { style: "medium", color: { rgb: "000000" } };

        // Apply borders to all sides - start with thin borders
        const borderConfig: ExcelBorderConfig = {
          top: thinBorderStyle,
          bottom: thinBorderStyle,
          left: thinBorderStyle,
          right: thinBorderStyle
        };

        // Apply thicker borders to outer edges for better table definition
        if (row === startRow) {
          borderConfig.top = mediumBorderStyle;
        }
        if (row === endRow) {
          borderConfig.bottom = mediumBorderStyle;
        }
        if (col === startCol) {
          borderConfig.left = mediumBorderStyle;
        }
        if (col === endCol) {
          borderConfig.right = mediumBorderStyle;
        }

        // Apply the border configuration to the cell
        this.worksheet[cellAddress].s.border = borderConfig;
      }
    }
  }
}
